import { Alert, Button, Form, Select } from "antd";
import PropTypes from "prop-types";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useCarPricingMode from "../../../../cars/hooks/useCarPricingMode";

/** Rate tiers rendered in the price description (generic across pricing modes). */
const RATE_TIERS = [
    { key: "hourlyRate", labelKey: "carSwapModal.hourlyRate" },
    { key: "dailyRate", labelKey: "carSwapModal.dailyRate" },
    { key: "nightlyRate", labelKey: "carSwapModal.nightlyRate" },
    { key: "weeklyRate", labelKey: "carSwapModal.weeklyRate" },
    { key: "monthlyRate", labelKey: "carSwapModal.monthlyRate" },
];

const formatRateAmount = (rate) => {
    const amount = Number(rate);
    if (Number.isNaN(amount)) {
        return rate;
    }
    return amount.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const CarSwapRatesPanel = ({ activeTiers, rateSource, t }) => (
    <Form.Item label={t("carSwapModal.rates")} className="!mb-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-px sm:overflow-hidden sm:rounded-lg sm:border sm:border-[#f0f0f0] sm:bg-[#fafafa]">
            {activeTiers.map(({ key, labelKey }) => (
                <div
                    key={key}
                    className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-[#f0f0f0] bg-white px-3 py-3 sm:rounded-none sm:border-0 sm:px-4 sm:py-3.5">
                    <span className="shrink-0 text-base text-[rgba(0,0,0,0.65)]">{t(labelKey)}</span>
                    <span className="fontSemi shrink-0 text-base tabular-nums text-[rgba(0,0,0,0.88)]">
                        ${formatRateAmount(rateSource[key].rate)}
                    </span>
                </div>
            ))}
        </div>
    </Form.Item>
);

CarSwapRatesPanel.propTypes = {
    activeTiers: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            labelKey: PropTypes.string.isRequired,
        }),
    ).isRequired,
    rateSource: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const hasActiveRates = (rateSource) =>
    RATE_TIERS.some(({ key }) => {
        const tier = rateSource?.[key];
        return tier && !tier.disabled && Number(tier.rate) > 0;
    });

const getActiveTiers = (rateSource) =>
    RATE_TIERS.filter(({ key }) => {
        const tier = rateSource?.[key];
        return tier && !tier.disabled && Number(tier.rate) > 0;
    });

const findCarBySelection = (cars, selectedId) =>
    cars.find((car) => car.id === selectedId || String(car._id) === String(selectedId));

const SelectCar = ({
    form,
    categories = [],
    availableCars = [],
    selectedCarId = null,
    isLoading = false,
    isCategoriesLoading = false,
    onCarChange,
    onMissingRatesChange,
    onEditCategoryRates,
}) => {
    const { t } = useTranslation(["reservations"]);
    const isCarPricingMode = useCarPricingMode();

    const [searchText, setSearchText] = React.useState("");
    const [searchCar, setSearchCar] = React.useState("");

    // Reactive watch — re-renders whenever the category field changes.
    const selectedCategoryId = Form.useWatch("category", form);

    const selectedCar = findCarBySelection(availableCars, selectedCarId);
    const selectedCategory = useMemo(
        () => categories.find((category) => String(category._id) === String(selectedCategoryId)),
        [categories, selectedCategoryId],
    );

    // Car mode: rates live on the car. Category mode: rates live on the category.
    const rateSource = isCarPricingMode ? selectedCar?.rate : selectedCategory || selectedCar?.category;
    const activeTiers = getActiveTiers(rateSource);
    const ratesAvailable = hasActiveRates(rateSource);

    // Block next when the relevant selection is made but has no active rates.
    const missingRates = isCarPricingMode
        ? Boolean(selectedCarId && !ratesAvailable)
        : Boolean(selectedCategoryId && !ratesAvailable);

    useEffect(() => {
        onMissingRatesChange?.(missingRates);
    }, [missingRates, onMissingRatesChange]);

    // Filter cars by the currently selected category ObjectId.
    const filteredCars = selectedCategoryId
        ? availableCars.filter((car) => String(car.category?._id) === selectedCategoryId)
        : [];

    const handleCategoryChange = (value) => {
        form.setFieldsValue({ category: value, car: null });
        setSearchText("");
        onCarChange?.(null);
    };

    const handleCarChange = (value) => {
        setSearchCar("");
        onCarChange?.(value);
    };

    const categoryIdForEdit = selectedCategory?.categoryId || selectedCategory?._id;

    return (
        <>
            <Form.Item
                label={t("carSwapModal.categoryLabel")}
                name="category"
                rules={[
                    {
                        required: true,
                        message: t("carSwapModal.categoryRequired"),
                    },
                ]}>
                <Select
                    placeholder={
                        isCategoriesLoading ? t("carSwapModal.categoryLoading") : t("carSwapModal.categoryPlaceholder")
                    }
                    onChange={handleCategoryChange}
                    showSearch
                    allowClear
                    loading={isCategoriesLoading}
                    searchValue={searchText}
                    autoClearSearchValue={true}
                    onSearch={(value) => setSearchText(value)}
                    filterOption={(input, option) => option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    options={categories.map((category) => ({
                        value: String(category._id),
                        label: category.categoryName,
                    }))}
                    notFoundContent={
                        isCategoriesLoading
                            ? t("carSwapModal.categoryLoading")
                            : categories.length === 0
                              ? t("carSwapModal.categoryNotFound")
                              : t("carSwapModal.categoryNotMatched")
                    }
                />
            </Form.Item>

            <Form.Item
                label={t("carSwapModal.carSelect")}
                name="car"
                rules={[
                    {
                        required: true,
                        message: t("carSwapModal.carSelectRequired"),
                    },
                ]}>
                <Select
                    placeholder={
                        isLoading ? t("carSwapModal.carSelectLoading") : t("carSwapModal.carSelectPlaceholder")
                    }
                    onChange={handleCarChange}
                    showSearch
                    allowClear
                    loading={isLoading}
                    disabled={!selectedCategoryId || isLoading}
                    searchValue={searchCar}
                    autoClearSearchValue={true}
                    onSearch={(value) => setSearchCar(value)}
                    filterOption={(input, option) => {
                        const carNumber = option.label.split("|")[0].trim() || "";
                        const carName = option.label.split("|")[1]?.trim() || "";

                        return (
                            carNumber.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                            carName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        );
                    }}
                    options={filteredCars.map((car) => ({
                        value: car._id,
                        label: `${car.carNumber} | ${car.carName}`,
                    }))}
                    notFoundContent={
                        isLoading
                            ? t("carSwapModal.carSelectLoading")
                            : selectedCategoryId && filteredCars.length === 0
                              ? t("carSwapModal.carSelectNotFound")
                              : t("carSwapModal.selectCategoryFirst")
                    }
                />
            </Form.Item>

            {/* Rates / no-rate guidance */}
            {isCarPricingMode && selectedCarId ? (
                ratesAvailable ? (
                    <CarSwapRatesPanel activeTiers={activeTiers} rateSource={rateSource} t={t} />
                ) : (
                    <Alert
                        className="mb-4"
                        type="warning"
                        showIcon
                        message={t("carSwapModal.noRate")}
                        description={t("carSwapModal.noCarRateDescription")}
                        action={
                            selectedCar?.VIN ? (
                                <Link to={`/cardetails/${selectedCar.VIN}?edit=true`} target="_blank" rel="noopener noreferrer">
                                    <Button size="small" type="primary" shape="round">
                                        {t("carSwapModal.goToCarDetails")}
                                    </Button>
                                </Link>
                            ) : null
                        }
                    />
                )
            ) : null}

            {!isCarPricingMode && selectedCategoryId ? (
                ratesAvailable ? (
                    selectedCarId ? (
                        <CarSwapRatesPanel activeTiers={activeTiers} rateSource={rateSource} t={t} />
                    ) : null
                ) : (
                    <Alert
                        className="mb-4"
                        type="warning"
                        showIcon
                        message={t("carSwapModal.noCategoryRate")}
                        description={t("carSwapModal.noCategoryRateDescription")}
                        action={
                            categoryIdForEdit ? (
                                <Button
                                    size="small"
                                    type="primary"
                                    shape="round"
                                    onClick={() => onEditCategoryRates?.(String(categoryIdForEdit))}>
                                    {t("carSwapModal.goToCategoryRates")}
                                </Button>
                            ) : null
                        }
                    />
                )
            ) : null}
        </>
    );
};

SelectCar.propTypes = {
    form: PropTypes.object.isRequired,
    categories: PropTypes.array,
    availableCars: PropTypes.array,
    selectedCarId: PropTypes.string,
    isLoading: PropTypes.bool,
    isCategoriesLoading: PropTypes.bool,
    onCarChange: PropTypes.func,
    onMissingRatesChange: PropTypes.func,
    onEditCategoryRates: PropTypes.func,
};

export default SelectCar;
